import React, { useState, useEffect } from 'react';
import { Heart, Users, Home, Eye, HelpCircle, Loader } from 'lucide-react';
import apiClient from "../../api/client";
import { toast } from 'sonner';


/**
 * Relationship Profile Section
 * 
 * Allows users to define their relationship practice, goals, household
 * preferences, and comfort levels with non-monogamy.
 * 
 * This makes Unity Love truly poly-first.
 */
export function RelationshipProfileSection({ token, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Dropdown options from backend
  const [options, setOptions] = useState({
    relationship_styles: [],
    relationship_goals: [],
    household_preferences: [],
    non_monogamy_comfort_levels: [],
    visibility_options: [],
  });
  
  // Form state
  const [relationshipStyle, setRelationshipStyle] = useState('');
  const [relationshipGoals, setRelationshipGoals] = useState([]);
  const [householdPreference, setHouseholdPreference] = useState('');
  const [nonMonogamyComfort, setNonMonogamyComfort] = useState('');
  const [relationshipVisibility, setRelationshipVisibility] = useState('');
  
  // Load options and current profile
  useEffect(() => {
    async function loadData() {
      try {
        // Load dropdown options
        const optionsRes = await apiClient.get(`/relationship/options`);
        setOptions(optionsRes.data);
        
        // Load current profile settings
        const profileRes = await apiClient.get(`/relationship/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const profile = profileRes.data;
        setRelationshipStyle(profile.relationship_style || '');
        setRelationshipGoals(profile.relationship_goals || []);
        setHouseholdPreference(profile.household_preference || '');
        setNonMonogamyComfort(profile.non_monogamy_comfort || '');
        setRelationshipVisibility(profile.relationship_visibility || '');
      } catch (err) {
        console.error('Failed to load relationship profile:', err);
        setError('Failed to load relationship settings');
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [token]);
  
  const handleGoalToggle = (goalValue) => {
    setRelationshipGoals(prev => {
      if (prev.includes(goalValue)) {
        return prev.filter(g => g !== goalValue);
      } else {
        return [...prev, goalValue];
      }
    });
  };
  
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await apiClient.put(
        `/relationship/profile`,
        {
          relationship_style: relationshipStyle || null,
          relationship_goals: relationshipGoals.length > 0 ? relationshipGoals : null,
          household_preference: householdPreference || null,
          non_monogamy_comfort: nonMonogamyComfort || null,
          relationship_visibility: relationshipVisibility || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Relationship settings saved!');
      if (onSave) onSave();
    } catch (err) {
      console.error('Failed to save relationship profile:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to save settings';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
        <div className="text-gray-400 text-center">Loading relationship settings...</div>
      </div>
    );
  }
  
  const isIncomplete = !relationshipStyle || relationshipGoals.length === 0;

  return (
    <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-400" />
            Relationship Style & Structure
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Tell us how you practice relationships so we can connect you with compatible people
          </p>
        </div>
      </div>

      {/* Inline hint for incomplete profiles */}
      {isIncomplete && (
        <div className="rounded-xl bg-slate-800/50 border border-slate-700 px-3 py-2.5 text-xs text-slate-300">
          💡 Adding your relationship style and goals helps Unity Love match you with
          people who want the same structure (triads, families, solo poly, etc.).
        </div>
      )}
      
      {error && (
        <div className="rounded-lg bg-red-900/30 border border-red-500 px-4 py-3 text-red-200 text-sm">
          {error}
        </div>
      )}
      
      {/* Relationship Style */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          <Users className="w-4 h-4 inline mr-2" />
          How do you practice relationships?
        </label>
        <select
          value={relationshipStyle}
          onChange={(e) => setRelationshipStyle(e.target.value)}
          className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2.5 text-white focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
        >
          <option value="">Select your relationship style...</option>
          {options.relationship_styles.map(style => (
            <option key={style.value} value={style.value}>
              {style.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1.5">
          This helps us understand your relationship philosophy
        </p>
      </div>
      
      {/* Relationship Goals (Multi-select) */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          What types of connections are you open to? (select all that apply)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto bg-gray-800/50 rounded-lg p-3 border border-gray-700">
          {options.relationship_goals.map(goal => (
            <button
              key={goal.value}
              type="button"
              onClick={() => handleGoalToggle(goal.value)}
              className={`text-left text-sm px-3 py-2 rounded-lg transition-all ${
                relationshipGoals.includes(goal.value)
                  ? 'bg-rose-600 text-white border border-rose-500'
                  : 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-gray-500'
              }`}
            >
              {goal.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1.5">
          Selected: {relationshipGoals.length} goal{relationshipGoals.length !== 1 ? 's' : ''}
        </p>
      </div>
      
      {/* Household Preference */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          <Home className="w-4 h-4 inline mr-2" />
          Household & living preference
        </label>
        <select
          value={householdPreference}
          onChange={(e) => setHouseholdPreference(e.target.value)}
          className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2.5 text-white focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
        >
          <option value="">Select household preference...</option>
          {options.household_preferences.map(pref => (
            <option key={pref.value} value={pref.value}>
              {pref.label}
            </option>
          ))}
        </select>
      </div>
      
      {/* Non-Monogamy Comfort */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          <HelpCircle className="w-4 h-4 inline mr-2" />
          Your comfort with non-monogamy
        </label>
        <select
          value={nonMonogamyComfort}
          onChange={(e) => setNonMonogamyComfort(e.target.value)}
          className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2.5 text-white focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
        >
          <option value="">Select comfort level...</option>
          {options.non_monogamy_comfort_levels.map(comfort => (
            <option key={comfort.value} value={comfort.value}>
              {comfort.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1.5">
          Helps us match you with people on a similar journey
        </p>
      </div>
      
      {/* Visibility */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          <Eye className="w-4 h-4 inline mr-2" />
          Who can see your relationship style?
        </label>
        <select
          value={relationshipVisibility}
          onChange={(e) => setRelationshipVisibility(e.target.value)}
          className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2.5 text-white focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
        >
          <option value="">Select visibility...</option>
          {options.visibility_options.map(vis => (
            <option key={vis.value} value={vis.value}>
              {vis.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1.5">
          Control your privacy - you can always change this later
        </p>
      </div>
      
      {/* Save Button */}
      <div className="pt-4 border-t border-gray-700">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-rose-600 to-pink-600 px-6 py-3 text-sm font-semibold text-white hover:from-rose-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50 disabled:opacity-50 disabled:cursor-wait transition-all"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Relationship Settings'
          )}
        </button>
      </div>
    </div>
  );
}
